/**
 * Persistence for the desktop rail's expanded/collapsed state.
 *
 * Pure and storage-injected (no `window`, no React) so it is unit-testable
 * the same way as lib/library/gatewayLibraryMapping.ts, and so a browser with
 * storage disabled — Safari private mode throws on `setItem`, not just on
 * read — collapses to the default instead of taking the rail down with it.
 */

export const RAIL_EXPANDED_KEY = 'baba.rail.expanded'

/** Collapsed: the 52px icon rail this app has always shipped. */
export const RAIL_WIDTH_COLLAPSED = 52
/** Expanded: wide enough for the plan card and a one-line email. */
export const RAIL_WIDTH_EXPANDED = 248

export interface RailStorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * Collapsed is the default: it is the layout every existing user already has,
 * and an unreadable or absent preference should never silently widen the app
 * chrome by 148px on their next visit.
 */
export function readRailExpanded(storage: RailStorageLike | null | undefined): boolean {
  try {
    return storage?.getItem(RAIL_EXPANDED_KEY) === '1'
  } catch {
    return false
  }
}

export function writeRailExpanded(storage: RailStorageLike | null | undefined, expanded: boolean): void {
  try {
    storage?.setItem(RAIL_EXPANDED_KEY, expanded ? '1' : '0')
  } catch {
    // Storage unavailable (private mode, quota) — the rail still works for
    // this session; it just starts collapsed on the next one.
  }
}

export function railWidth(expanded: boolean): number {
  return expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COLLAPSED
}

/**
 * Left edge for a popover anchored to the rail (the mobile-app sheet, the
 * account menu). It has to move with the rail or the panel overlaps the
 * labels when expanded.
 */
export function railPopoverLeft(expanded: boolean): number {
  return railWidth(expanded) + 4
}
